import {useMemo} from 'react';

import {useQuickAddMetricSearchQuery} from '../../../../generated/graphql';
import {useProjectParams} from '../../../../pages/ProjectPage/useProjectParams';
import {useSelector} from '../../../../state/hooks';
import {useHistoryKeyInfoQueryVars} from '../../../../state/runs/hooks';
import {toMongo} from '../../../../util/filters';
import {useKeyInfoQueryContext} from '../../../MultiRunWorkspace/KeyInfoQueryContext';
import {APIAddedPanelSpecs, DefaultPanelSpecs} from '../../../PanelBank/types';
import {QuickAddKeys} from '../PanelsQuickAddContext';

const METRICS_SEARCH_RUN_LIMIT = 100;

interface ExpectedPanels {
  defaultSpecs: DefaultPanelSpecs;
  apiAddedSpecs: APIAddedPanelSpecs;
}

let memoizedExpectedPanels: ExpectedPanels | null = null;
let memoizedQuickAddKeys: QuickAddKeys | null = null;

function memoizedSortedQuickAddKeys(expectedPanels: ExpectedPanels) {
  if (expectedPanels === memoizedExpectedPanels && memoizedQuickAddKeys) {
    return memoizedQuickAddKeys;
  }

  const {defaultSpecs, apiAddedSpecs} = expectedPanels;
  const result = [
    ...Object.entries(defaultSpecs),
    ...Object.entries(apiAddedSpecs),
  ].sort((a, b) => a[0].localeCompare(b[0]));

  memoizedExpectedPanels = expectedPanels;
  memoizedQuickAddKeys = result;
  return result;
}

export const useSearchedKeys = (searchStr: string) => {
  const expectedPanels = useSelector(state => state.expectedPanels);

  const allQuickAddKeys = useMemo(() => {
    if (!expectedPanels) {
      return [];
    }

    return memoizedSortedQuickAddKeys(expectedPanels);
  }, [expectedPanels]);

  return useMemo(() => {
    try {
      if (!searchStr) {
        return allQuickAddKeys;
      }
      const regex = new RegExp(searchStr, 'i');
      return allQuickAddKeys.filter(([key]) => regex.test(key));
    } catch (e) {
      console.log('error while running regex: ', {e});
      return [];
    }
  }, [allQuickAddKeys, searchStr]);
};

interface PaginatedResult {
  keys: QuickAddKeys;
  hasNextPage: boolean;
  loading: boolean;
  error?: any;
  nextCursor: string | null;
}

export const useSearchedKeysPaginated = (
  searchStr: string,
  cursor: string | null,
  pageSize: number
): PaginatedResult => {
  const {projectName, entityName} = useProjectParams();
  const {runSetRefs, customRunNamesRef} = useKeyInfoQueryContext();

  const {filters} = useHistoryKeyInfoQueryVars(runSetRefs, customRunNamesRef);

  // Direct query call without any skipping or prefetching
  const {data, loading, error} = useQuickAddMetricSearchQuery({
    variables: {
      projectName,
      entityName,
      metricNamePattern: searchStr,
      cursor,
      keysPageSize: pageSize,
      runLimit: METRICS_SEARCH_RUN_LIMIT,
      filters: JSON.stringify(toMongo(filters)),
    },
  });

  // Transform results into QuickAddKeys format
  const keys = useMemo(() => {
    if (!data?.project?.runs?.metricSearch?.results) {
      return [];
    }

    return data.project.runs.metricSearch.results.map(
      (result: any) =>
        [result.key, {column: result.column, types: result.types}] as [
          string,
          any
        ]
    );
  }, [data]);

  // Determine if there's a next page
  const hasNextPage = data?.project?.runs?.metricSearch?.hasMore ?? false;

  // Get next cursor
  const nextCursor = data?.project?.runs?.metricSearch?.cursor || null;

  return {
    keys,
    hasNextPage,
    loading,
    error,
    nextCursor,
  };
};
